import { useState } from 'react';
import { useGetEducationClassListQuery } from 'shared/api';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import type { EducationClass } from 'shared/api';
import type { TablePaginationConfig, TableProps } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import { getEducationClassesTableColumns } from './columns';
import { educationClassesTableSelectors } from './selectors';
import { educationClassesTableActions } from './slice';

export const useEducationClassesTable = () => {
  const dispatch = useAppDispatch();

  const tableParams = useAppSelector(educationClassesTableSelectors.getTableParams);
  const tableFilters = useAppSelector(educationClassesTableSelectors.getTableFilters);

  const { data: educationClasses, isLoading } = useGetEducationClassListQuery({
    ...tableParams,
    ...tableFilters,
  });

  const allColumns = getEducationClassesTableColumns();

  const [displayedColumns, setDisplayedColumns] = useState(allColumns);

  const pagination: TablePaginationConfig = {
    current: (tableParams.page || 0) + 1,
    pageSize: tableParams.size || 10,
    total: educationClasses?.totalElements || 0,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} из ${total}`,
    position: ['bottomCenter'],
  };

  const onTableChange: TableProps<EducationClass>['onChange'] = (
    nextPagination,
    filters,
    sorter,
  ) => {
    dispatch(
      educationClassesTableActions.setTableParams({
        pagination: nextPagination,
        filters,
        sorter: {
          ...sorter,
          columnKey: undefined,
          column: undefined,
        },
      }),
    );
  };

  const onColumnsChange = (columns: ColumnType<EducationClass>[]) => {
    setDisplayedColumns(columns);
  };

  const onFilterChange = (values: Record<string, unknown>) => {
    const { includeSaturday, ...rest } = values;

    dispatch(
      educationClassesTableActions.setFilters({
        ...rest,
        ...(includeSaturday !== undefined && {
          includeSaturday: includeSaturday === true || includeSaturday === 'true',
        }),
      }),
    );
  };

  return {
    state: {
      educationClasses: educationClasses?.content || [],
      allColumns,
      isLoading,
      pagination,
      displayedColumns,
    },
    methods: {
      onTableChange,
      onColumnsChange,
      onFilterChange,
    },
  };
};
